<script setup lang="ts">import { computed as _computed } from 'vue';

import type { DescriptionDto } from '@/api';
import { getProviderIconSrc } from '@/utils/provider-icons';
import SIcon from '@/components/SIcon.vue';
import ExternalDescription from './External-Description.vue';

interface ExternalDescriptionsProps {
  descriptions: DescriptionDto[];
  textSearch?: string;
  asCard?: boolean;
  shouldShowRichtext?: boolean;
}

const props = defineProps({
  descriptions: null,
  textSearch: { default: '' },
  asCard: { type: Boolean, default: false },
  shouldShowRichtext: { type: Boolean, default: false }
});

const outlined = _computed(() => props.asCard);
const backgroundColor = _computed(() => (props.asCard ? 'var(--v-bgPageSecondary-base)' : 'transparent'));

const groupedDescriptionsByText = _computed(() => {
  const textToSourceTypes = new Map<string, DescriptionDto.sourceType[]>();

  props.descriptions.forEach((desc) => {
    if (desc.sourceType) {
      const text = desc.description || '';

      if (!textToSourceTypes.has(text)) {
        textToSourceTypes.set(text, []);
      }

      const sourceTypes = textToSourceTypes.get(text)!;
      if (!sourceTypes.includes(desc.sourceType)) {
        sourceTypes.push(desc.sourceType);
      }
    }
  });

  return Array.from(textToSourceTypes.entries()).map(([text, sourceTypes]) => ({
    text,
    sourceTypes,
  }));
});
</script>

<template lang="pug">
v-card(v-if="descriptions.length" :class="{ 'pa-2': asCard, 'my-1': true }" :outlined="outlined" :style="{ backgroundColor }")
  .group(v-for="(group, i) in groupedDescriptionsByText" :key="i")
    .d-flex.align-start
      .icon-group.d-flex.mr-2
        SIcon.mr-1(
          v-for="sourceType in group.sourceTypes"
          :key="sourceType"
          size="small"
          :icon="getProviderIconSrc(`${sourceType}_LOCK`)"
          :alt="sourceType"
        )

      ExternalDescription.flex-grow-1(
        :description="{ sourceType: group.sourceTypes[0], description: group.text }"
        :search="textSearch"
        :should-show-richtext="shouldShowRichtext"
        hide-icon
      )
</template>
